import { Entity, Location } from '@nx-smartmonkey/shared/domain';
import { removeEmpty } from '@nx-smartmonkey/shared/helpers';

import { has } from 'lodash';
import { Subscription } from '../subscription/Subscription';

export const numberOfVehicles = [`one`, `less_than_5`, `between_5_and_10`, `more_than_10`] as const;

export type NumOfVehiclesTypes = (typeof numberOfVehicles)[number];

export interface UpdateOrganizationProps {
  label?: string | null;
  country?: string | null;
  num_of_vehicles?: NumOfVehiclesTypes;
  goal?: string[];
  section?: string;
  default_project_id?: string;
  location?: Location;
  active?: boolean;
}

export interface OrganizationProps {
  avatar_url?: string;
  label?: string;
  active: boolean;
  country?: string;
  owner_id: string;
  default_project_id: string;
  location?: Location;
  subscription: Subscription;
  num_of_vehicles: NumOfVehiclesTypes;
  goal: string[];
  section: string;

  deleted?: boolean;
  created_by: string;
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
}

export class Organization extends Entity<OrganizationProps> {
  private _avatar: string | undefined;

  get id(): string {
    return this._id!;
  }

  get avatar_url(): string | undefined {
    return this.props.avatar_url;
  }

  set avatar_url(avatarUrl: string | undefined) {
    this.props.avatar_url = avatarUrl;
  }

  get organization_avatar(): string | undefined {
    return this._avatar;
  }

  set organization_avatar(organizationAvatar: string | undefined) {
    this._avatar = organizationAvatar;
  }

  get label(): string | undefined {
    return this.props.label;
  }

  set label(label: string | undefined) {
    this.props.label = label;
  }

  get active(): boolean {
    return this.props.active;
  }

  set active(active: boolean) {
    this.props.active = active;
  }

  get country(): string | undefined {
    return this.props.country;
  }

  set country(country: string | undefined) {
    this.props.country = country;
  }

  get country_code(): string | undefined {
    return this.props.location?.country_code ?? this.props.country;
  }

  get owner_id(): string {
    return this.props.owner_id;
  }

  set owner_id(ownerId: string) {
    this.props.owner_id = ownerId;
  }

  get default_project_id(): string {
    return this.props.default_project_id;
  }

  set default_project_id(defaultProjectId: string) {
    this.props.default_project_id = defaultProjectId;
  }

  get location(): Location | undefined {
    return this.props.location;
  }

  set location(location: Location | undefined) {
    this.props.location = location;
  }

  get subscription(): Subscription {
    return this.props.subscription;
  }

  set subscription(subscription: Subscription) {
    this.props.subscription = subscription;
  }

  get num_of_vehicles(): NumOfVehiclesTypes {
    return this.props.num_of_vehicles;
  }

  set num_of_vehicles(numOfVehicles: NumOfVehiclesTypes) {
    this.props.num_of_vehicles = numOfVehicles;
  }

  get goal(): string[] {
    return this.props.goal;
  }

  set goal(goal: string[]) {
    this.props.goal = goal;
  }

  get section(): string {
    return this.props.section;
  }

  set section(section: string) {
    this.props.section = section;
  }

  get deleted(): boolean | undefined {
    return this.props.deleted;
  }

  get created_by(): string {
    return this.props.created_by;
  }

  get created_at(): Date | undefined {
    return this.props.created_at;
  }

  get updated_at(): Date | undefined {
    return this.props.updated_at;
  }

  get deleted_at(): Date | undefined {
    return this.props.deleted_at;
  }

  protected constructor(props: OrganizationProps, id: string) {
    super(props, id);
  }

  static create(props: OrganizationProps, id: string): Organization {
    return new Organization(props, id);
  }

  update(props: UpdateOrganizationProps) {
    removeEmpty(props);
    if (props.label) this.label = props.label;
    if (props.country) this.country = props.country;
    if (props.num_of_vehicles) this.num_of_vehicles = props.num_of_vehicles;
    if (props.goal) this.goal = props.goal;
    if (props.section) this.section = props.section;
    if (props.default_project_id) this.default_project_id = props.default_project_id;
    if (has(props, `active`)) this.active = props.active!;
    if (props.location) this.location = props.location;
  }

  /**
   * Checks if user is owner of the organization.
   *
   * @param user
   */
  isOwner(userId?: string): boolean {
    return this.owner_id === userId;
  }
}
